(() => {
  const shelfSelector = '[data-sale-shelf]';
  const mobileViewport = window.matchMedia('(max-width: 749px)');
  const desktopViewport = window.matchMedia('(min-width: 990px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initializedShelves = new WeakSet();

  class MobileSaleShelfRotation {
    constructor(shelf) {
      this.shelf = shelf;
      this.track = shelf.querySelector('.sale-product-shelf__track');
      this.items = Array.from(shelf.querySelectorAll('.sale-product-shelf__item'));
      this.toggle = shelf.querySelector('[data-sale-rotation-toggle]');
      this.toggleLabel = shelf.querySelector('[data-sale-rotation-label]');
      this.counter = shelf.querySelector('[data-sale-counter]');
      this.startTimer = null;
      this.rotationTimer = null;
      this.resumeTimer = null;
      this.visible = false;
      this.userPaused = false;
      this.interacting = false;

      if (!this.track || this.items.length < 2) return;

      this.bindEvents();
      this.observeVisibility();
      this.sync();
    }

    bindEvents() {
      this.toggle?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.updateToggle();
        this.sync();
      });

      this.track.addEventListener('pointerdown', () => {
        this.interacting = true;
        this.clearTimers();
      });

      ['pointerup', 'pointercancel'].forEach((eventName) => {
        this.track.addEventListener(eventName, () => this.resumeAfterInteraction());
      });

      this.track.addEventListener('scroll', () => {
        if (!this.interacting) return;
        this.resumeAfterInteraction();
      }, { passive: true });

      this.track.addEventListener('wheel', (event) => this.routeDesktopWheel(event), { passive: false });

      this.shelf.addEventListener('focusin', (event) => {
        if (event.target === this.toggle) return;
        this.interacting = true;
        this.clearTimers();
      });

      this.shelf.addEventListener('focusout', () => {
        window.requestAnimationFrame(() => {
          if (this.shelf.contains(document.activeElement) && document.activeElement !== this.toggle) return;
          this.resumeAfterInteraction();
        });
      });

      document.addEventListener('visibilitychange', () => this.sync());
      this.addMediaListener(mobileViewport);
      this.addMediaListener(reducedMotion);
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) {
        this.visible = true;
        return;
      }

      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        this.sync();
      }, { threshold: [0, 0.5] });
      this.observer.observe(this.shelf);
    }

    addMediaListener(query) {
      const listener = () => this.sync();
      query.addEventListener ? query.addEventListener('change', listener) : query.addListener(listener);
    }

    isEligible() {
      return mobileViewport.matches && !reducedMotion.matches && this.track.scrollWidth > this.track.clientWidth + 2;
    }

    routeDesktopWheel(event) {
      if (!desktopViewport.matches || event.ctrlKey || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      const maximum = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const movingRight = event.deltaY > 0;
      const canMove = movingRight ? this.track.scrollLeft < maximum - 2 : this.track.scrollLeft > 2;
      if (!canMove) return;

      event.preventDefault();
      this.track.scrollBy({ left: event.deltaY, behavior: 'auto' });
    }

    canRotate() {
      return this.isEligible() && this.visible && !this.userPaused && !this.interacting && !document.hidden;
    }

    sync() {
      this.clearTimers();
      if (this.toggle) this.toggle.hidden = !this.isEligible();
      if (this.counter) this.counter.setAttribute('aria-live', this.canRotate() ? 'off' : 'polite');
      this.updateToggle();
      if (this.canRotate()) this.startTimer = window.setTimeout(() => this.startRotation(), 8000);
    }

    startRotation() {
      if (!this.canRotate()) return;
      this.advance();
      this.rotationTimer = window.setInterval(() => {
        if (this.canRotate()) this.advance();
      }, 4000);
    }

    advance() {
      const maximum = Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
      const step = this.items[1].offsetLeft - this.items[0].offsetLeft;
      const next = this.track.scrollLeft >= maximum - 2 ? 0 : Math.min(this.track.scrollLeft + step, maximum);
      this.track.scrollTo({ left: next, behavior: 'smooth' });
    }

    resumeAfterInteraction() {
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => {
        this.interacting = false;
        this.sync();
      }, 1200);
    }

    clearTimers() {
      window.clearTimeout(this.startTimer);
      window.clearInterval(this.rotationTimer);
      this.startTimer = null;
      this.rotationTimer = null;
    }

    updateToggle() {
      if (!this.toggle) return;
      this.toggle.setAttribute('aria-label', this.userPaused ? 'Resume automatic sale product rotation' : 'Pause automatic sale product rotation');
      if (this.toggleLabel) this.toggleLabel.textContent = this.userPaused ? 'Resume rotation' : 'Pause rotation';
    }
  }

  const initialize = (scope = document) => {
    const shelves = scope.matches?.(shelfSelector) ? [scope] : Array.from(scope.querySelectorAll(shelfSelector));
    shelves.forEach((shelf) => {
      if (initializedShelves.has(shelf)) return;
      initializedShelves.add(shelf);
      new MobileSaleShelfRotation(shelf);
    });
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
